import { Card, Empty, Space, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Button } from '~/app/components/common/Button';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { WizardData } from '~/app/components/maintenance/preventive/wizard/types';
import { useStepTasks } from '~/app/components/maintenance/preventive/wizard/StepTasks/hooks/useStepTasks';
import { StepTasksCard } from '~/app/components/maintenance/preventive/wizard/StepTasks/components/StepTasksCard/StepTasksCard';
import { StepTasksTaskBuilder } from '~/app/components/maintenance/preventive/wizard/StepTasks/components/StepTasksTaskBuilder/StepTasksTaskBuilder';
import { StepTasksTriggerBuilder } from '~/app/components/maintenance/preventive/wizard/StepTasks/components/StepTasksTriggerBuilder/StepTasksTriggerBuilder';

const { Text, Paragraph } = Typography;

const TASK_LIST_MAX_HEIGHT = '45vh';

type StepTasksProps = {
  data: WizardData;
  onChange: (patch: Partial<WizardData>) => void;
};

export function StepTasks({ data, onChange }: StepTasksProps) {
  const { t } = useTranslation('maintenance');
  const {
    suggestions,
    triggerOptions,
    workTypeOptions,
    partOptions,
    inlineTaskOpen,
    setInlineTaskOpen,
    inlineTriggerForTaskId,
    setInlineTriggerForTaskId,
    handleAddSuggestion,
    handleCreateTaskInline,
    handleRemove,
    handleTriggerChange,
    handleCreateTriggerInline,
    handleDragEnd,
    isCreatingTask,
    isCreatingTrigger,
  } = useStepTasks({ data, onChange });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <Card>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div>
          <Text strong>{t('wizard.tasks.suggestions')}</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {t('wizard.tasks.suggestionsHint')}
          </Paragraph>
          {suggestions.length === 0 ? (
            <Text type="secondary">{t('wizard.tasks.noSuggestions')}</Text>
          ) : (
            <Space wrap>
              {suggestions.map((suggestion) => (
                <Button
                  key={suggestion.taskId}
                  onClick={() => handleAddSuggestion(suggestion.taskId)}
                >
                  {suggestion.name}
                  {suggestion.workTypeLabel ? (
                    <Typography.Text
                      type="secondary"
                      style={{ marginInlineStart: 8 }}
                    >
                      {suggestion.workTypeLabel}
                    </Typography.Text>
                  ) : null}
                </Button>
              ))}
            </Space>
          )}
        </div>

        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text type="secondary">{t('wizard.tasks.dragToReorder')}</Text>
          {!inlineTaskOpen ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setInlineTriggerForTaskId(null);
                setInlineTaskOpen(true);
              }}
            >
              {t('wizard.tasks.createTask')}
            </Button>
          ) : null}
        </Space>

        {inlineTaskOpen ? (
          <StepTasksTaskBuilder
            workTypeOptions={workTypeOptions}
            partOptions={partOptions}
            loading={isCreatingTask}
            onCancel={() => setInlineTaskOpen(false)}
            onSubmit={handleCreateTaskInline}
          />
        ) : null}

        {data.tasks.length === 0 && !inlineTaskOpen ? (
          <Empty description={t('wizard.tasks.noTasks')} />
        ) : data.tasks.length > 0 ? (
          <div
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              maxHeight: TASK_LIST_MAX_HEIGHT,
              overflow: 'auto',
            }}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={data.tasks.map((task) => task.taskId)}
                strategy={verticalListSortingStrategy}
              >
                {data.tasks.map((task, index) => {
                  const isCreatingThisTrigger =
                    inlineTriggerForTaskId === task.taskId;
                  return (
                    <Fragment key={task.taskId}>
                      <StepTasksCard
                        task={task}
                        index={index}
                        triggerOptions={triggerOptions}
                        isCreatingTrigger={isCreatingThisTrigger}
                        onRemove={() => handleRemove(task.taskId)}
                        onTriggerChange={(triggerId) =>
                          handleTriggerChange(task.taskId, triggerId)
                        }
                        onCreateTrigger={() => {
                          setInlineTaskOpen(false);
                          setInlineTriggerForTaskId(
                            isCreatingThisTrigger ? null : task.taskId,
                          );
                        }}
                      />
                      {isCreatingThisTrigger ? (
                        <StepTasksTriggerBuilder
                          loading={isCreatingTrigger}
                          onCancel={() => setInlineTriggerForTaskId(null)}
                          onSubmit={(values) =>
                            handleCreateTriggerInline(task.taskId, values)
                          }
                        />
                      ) : null}
                    </Fragment>
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
        ) : null}
      </Space>
    </Card>
  );
}
